import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { type ResponseGenerics } from '@dni-types/utilities/response'
import { type ECommerceGetCouponIdByCodeErrorResponse } from '@hooks-api/types/apiErrorResponse'
import { AppliedStatus, type CartCalculateResult, type CartValidationDetail, CouponExceptionCode, type CouponExceptionCodeType } from '@hooks-api/types/dataModel/coupon'
import { type AxiosError, type AxiosPromise } from 'axios'
import { type FormEventHandler, useState } from 'react'
import { getCouponManualInputExceptionMessage, type GetCouponManualInputExceptionMessageReturn, isValidateErrorCode } from '../utils/errorHandlers'

export interface UseCouponManualInputArgs {
  getCouponIdByCode: (couponCode: string) => AxiosPromise<ResponseGenerics<{
    coupon_id: number
    display_name: string
  }>>
  currentManualInputCouponIds?: number[] | null
  calculateCoupons: (manualInputCouponIds: number[]) => AxiosPromise<ResponseGenerics<CartCalculateResult>>
  onApplyCoupon?: (manualInputCouponIds: number[], inputCouponId: number) => void
  onApplyCouponSuccess?: (message: string, manualInputCouponIds: number[]) => void
  onApplyCouponSuccessNotQualified?: (message: string, manualInputCouponIds: number[]) => void
  onApplyCouponError?: (message: string, manualInputCouponIds: number[]) => void
  onGetCouponError?: (exceptionMessage: GetCouponManualInputExceptionMessageReturn, code?: CouponExceptionCodeType) => void
}

export const useCouponManualInput = ({
  getCouponIdByCode,
  calculateCoupons,
  currentManualInputCouponIds,
  onApplyCoupon,
  onApplyCouponSuccess,
  onApplyCouponSuccessNotQualified,
  onApplyCouponError,
  onGetCouponError,
}: UseCouponManualInputArgs) => {
  const [couponCode, setCouponCode] = useState('')

  const handleCouponErrorMessages = useCallbackEvent((code?: CouponExceptionCodeType, detail?: CartValidationDetail) => {
    const exceptionMessage = getCouponManualInputExceptionMessage(code, detail)
    onGetCouponError?.(exceptionMessage, code)
  })

  const handleGetCouponByCode = useCallbackEvent(async () => {
    try {
      const { data } = await getCouponIdByCode(couponCode)
      return data.data
    }
    catch (error) {
      const errorResponse = error as AxiosError<ECommerceGetCouponIdByCodeErrorResponse>
      const errorCode = errorResponse.response?.data?.error?.code
      const detail = errorResponse.response?.data?.error?.detail
      handleCouponErrorMessages(errorCode, detail)
      return null
    }
  })

  const handleSubmit: FormEventHandler<HTMLFormElement> = useCallbackEvent(async (event) => {
    event.preventDefault()
    let manualInputCouponIds: number[] = []
    try {
      const coupon = await handleGetCouponByCode()
      if (!coupon) return

      manualInputCouponIds = [coupon.coupon_id]
      const { data: calculateResponse } = await calculateCoupons(
        /**
         * 送 calculate 的 manual_input_coupon_ids 需要包含目前的 couponId，才能判斷有沒有互斥的券
         */
        [...new Set(
          [...manualInputCouponIds, ...(currentManualInputCouponIds || [])],
        )],
      )
      const appliedCoupons = calculateResponse.data.applied_coupons.flatMap(appliedCoupon => appliedCoupon.criterions).filter(criterion => criterion.applied_status === AppliedStatus.APPLIED)
      const cartValidationMessages = calculateResponse.data.cart_validation_messages
      if (cartValidationMessages[0] && isValidateErrorCode(cartValidationMessages[0].code, CouponExceptionCode)) {
        handleCouponErrorMessages(cartValidationMessages[0].code, cartValidationMessages[0].message)
        return
      }

      onApplyCoupon?.(manualInputCouponIds, coupon.coupon_id)

      if (appliedCoupons.some(appliedCoupon => manualInputCouponIds.includes(appliedCoupon.coupon_id))) {
        onApplyCouponSuccess?.('優惠券已成功套用', manualInputCouponIds)
      }
      else {
        onApplyCouponSuccessNotQualified?.('優惠券套用成功，尚未符合條件', manualInputCouponIds)
      }
    }
    catch (error) {
      console.error(error)
      onApplyCouponError?.('優惠異常，請洽客服', manualInputCouponIds)
    }
  })

  return {
    couponCode,
    setCouponCode,
    handleSubmit,
  }
}
